import { useState } from 'react';
import { Card, Form, Button, Alert, Badge, ListGroup, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { reposService, ApiError } from '@/services/api';
import { useAuth } from '@/common/hooks/use-auth';
import { StackPicker, type SelectedStack } from '@/common/components/stack-picker';
import { EmailChipsInput } from '@/common/components/email-chips-input';

/**
 * Guided onboarding wizard. Steps:
 *   1. Repo        — confirmed from the query params (?workspace=&repo=).
 *   2. Hosting     — pick region → browse/filter CloudFormation stacks → select; repeat per region.
 *   3. Behavior    — default branch, owners (notifications), ai-context root, enabled.
 *   4. Review      — resources discovered from the selected stacks, then onboard.
 *
 * Composition (ECR repos / Lambdas) is read live from the selected stacks; we persist the
 * selected stack ARNs on the repo. Pattern ported (shape only) from ignite-frontend wizard.
 */

const STEPS = ['Repo', 'Hosting', 'Behavior', 'Review'] as const;

export default function OnboardWizard() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { actor } = useAuth();
  const workspace = params.get('workspace') ?? '';
  const repoSlug = params.get('repo') ?? '';
  const repo = `${workspace}/${repoSlug}`;

  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState<SelectedStack[]>([]);
  const [defaultBranch, setDefaultBranch] = useState(params.get('mainBranch') ?? '');
  const [owners, setOwners] = useState<string[]>([]);
  const [aiContextRoot, setAiContextRoot] = useState('ai_context');
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canNext =
    step === 0 ? Boolean(workspace && repoSlug)
    : step === 1 ? selected.length > 0
    : step === 2 ? defaultBranch.trim().length > 0
    : true;

  async function onboard() {
    setBusy(true);
    setErr(null);
    try {
      await reposService.createRepo(actor, {
        bitbucketWorkspace: workspace,
        bitbucketRepo: repoSlug,
        defaultBranch: defaultBranch.trim(),
        policy: 'assist',
        enabled,
        owners,
        aiContextRoot: aiContextRoot.trim() || 'ai_context',
        cloudformationStackArns: selected.map((s) => s.stackArn),
      });
      navigate(`/repos/detail?repo=${encodeURIComponent(repo)}`);
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <Card.Header>Onboard {repo || '(pick a repo first)'}</Card.Header>
      <Card.Body>
        {/* Stepper */}
        <div className="d-flex gap-2 mb-3">
          {STEPS.map((label, i) => (
            <Badge key={label} bg={i === step ? 'primary' : i < step ? 'success' : 'secondary'}>
              {i + 1}. {label}
            </Badge>
          ))}
        </div>

        {err && <Alert variant="danger">{err}</Alert>}

        {step === 0 && (
          <div>
            <p>Onboarding Bitbucket repo <strong>{repo}</strong>.</p>
            {!workspace || !repoSlug ? (
              <Alert variant="warning">
                Open this wizard from a repo in the workspace browser so the repo is set.
              </Alert>
            ) : (
              <p className="text-muted">Next, tell us where this app is hosted so we can discover its resources.</p>
            )}
          </div>
        )}

        {step === 1 && (
          <div>
            <p className="text-muted">
              Pick the AWS region where this app is hosted, then select the CloudFormation stack(s)
              that make it up. Hosted in more than one region? Load another region and select there
              too — all selections are kept.
            </p>
            <StackPicker selected={selected} setSelected={setSelected} />
          </div>
        )}

        {step === 2 && (
          <div>
            <Form.Group className="mb-3">
              <Form.Label>Default branch</Form.Label>
              <Form.Control
                value={defaultBranch}
                placeholder="e.g. main, sandbox, develop"
                onChange={(e) => setDefaultBranch(e.target.value)}
              />
              <Form.Text className="text-muted">
                The branch fixes branch off and PRs target — this repo's integration branch for this environment.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Owners</Form.Label>
              <EmailChipsInput
                value={owners}
                onChange={setOwners}
                placeholder="dev@genesys.com — press Enter to add"
              />
              <Form.Text className="text-muted">
                Responsible people — and who gets notified on a fix PR or drafted exception. Add a
                distribution list to alert a channel.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>AI context root</Form.Label>
              <Form.Control
                value={aiContextRoot}
                placeholder="ai_context"
                onChange={(e) => setAiContextRoot(e.target.value)}
              />
              <Form.Text className="text-muted">
                Where this repo keeps its security AI context, relative to the repo root (default <code>ai_context</code>).
              </Form.Text>
            </Form.Group>
            <Form.Check
              type="switch"
              label="Enabled"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <Form.Text className="text-muted d-block">
              When on, vuln-sentinel acts on Inspector findings for this repo.
            </Form.Text>
          </div>
        )}

        {step === 3 && (
          <ReviewStep
            repo={repo}
            selected={selected}
            defaultBranch={defaultBranch}
            owners={owners}
            enabled={enabled}
          />
        )}

        {/* Nav */}
        <div className="d-flex justify-content-between mt-4">
          <Button variant="outline-secondary" disabled={step === 0 || busy} onClick={() => setStep((s) => s - 1)}>
            Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button disabled={!canNext || busy} onClick={() => setStep((s) => s + 1)}>
              Next
            </Button>
          ) : (
            <Button variant="success" disabled={busy || !canNext} onClick={onboard}>
              {busy ? 'Onboarding…' : 'Onboard'}
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

// -- Review step: resources discovered from the selected stacks --

function ReviewStep({
  repo,
  selected,
  defaultBranch,
  owners,
  enabled,
}: {
  repo: string;
  selected: SelectedStack[];
  defaultBranch: string;
  owners: string[];
  enabled: boolean;
}) {
  const [resources, setResources] = useState<{ type: string; physicalId: string; region: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  async function discover() {
    setLoading(true);
    setError(null);
    try {
      const all: { type: string; physicalId: string; region: string }[] = [];
      for (const s of selected) {
        const res = await reposService.listStackResources(s.stackArn, s.region);
        for (const r of res.items ?? []) {
          if (r.type === 'AWS::ECR::Repository' || r.type === 'AWS::Lambda::Function') {
            all.push({ type: r.type, physicalId: r.physicalId, region: s.region });
          }
        }
      }
      setResources(all);
      setLoaded(true);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h5>Review</h5>
      <ListGroup className="mb-3">
        <ListGroup.Item><strong>Repo:</strong> {repo}</ListGroup.Item>
        <ListGroup.Item><strong>Default branch:</strong> {defaultBranch || '(not set)'}</ListGroup.Item>
        <ListGroup.Item><strong>Owners:</strong> {owners.length ? owners.join(', ') : '(none)'}</ListGroup.Item>
        <ListGroup.Item><strong>Enabled:</strong> {enabled ? 'yes' : 'no'}</ListGroup.Item>
        <ListGroup.Item><strong>Stacks:</strong> {selected.map((s) => `${s.stackName} (${s.region})`).join(', ')}</ListGroup.Item>
      </ListGroup>

      <Button variant="outline-primary" onClick={discover} disabled={loading} className="mb-3">
        {loading ? <Spinner size="sm" /> : 'Preview discovered resources'}
      </Button>
      {error && <Alert variant="danger">{error}</Alert>}
      {loaded && resources.length > 0 && (
        <p className="text-muted small mb-2">
          Found in your stacks. vuln-sentinel ingests and remediates{' '}
          <strong>ECR image</strong> and <strong>Lambda function</strong> findings — these
          resources will be monitored once you save.
        </p>
      )}
      {loaded && (
        resources.length ? (
          <ListGroup>
            {resources.map((r, i) => (
              <ListGroup.Item key={i} className="d-flex justify-content-between">
                <span>{r.physicalId}</span>
                <span>
                  <Badge bg="info">{r.type.replace('AWS::', '').replace('::', ' ')}</Badge>{' '}
                  <Badge bg="secondary">{r.region}</Badge>
                </span>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <Alert variant="warning">
            No Inspector-scannable resources found in the selected stacks. vuln-sentinel monitors
            ECR image repositories and Lambda functions — double-check you selected the stack(s)
            that contain this app's images and/or functions.
          </Alert>
        )
      )}
    </div>
  );
}
